// src/utils/api/setupWizard/types.js

export class SetupConfig {
  constructor(data) {
    this.companyName = data.company_name || '';
    this.primaryDomain = data.primary_domain || '';
    this.tagline = data.tagline || '';
    this.logoUrl = data.logo_url || '';
    this.backgroundUrl = data.background_url || '';
    this.primaryColor = data.primary_color || '#000000';
    this.secondaryColor = data.secondary_color || '#ffffff';
    this.apiBaseUrl = data.api_base_url || '';
    this.defaultLocale = data.default_locale || 'en-US';
    this.supportEmail = data.support_email || '';
    this.experimentalFeatures = data.experimental_features || false;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
  }

  static fromApiResponse(data) {
    return new SetupConfig(data);
  }

  toApiFormat() {
    return {
      company_name: this.companyName,
      primary_domain: this.primaryDomain,
      tagline: this.tagline,
      logo_url: this.logoUrl,
      background_url: this.backgroundUrl,
      primary_color: this.primaryColor,
      secondary_color: this.secondaryColor,
      api_base_url: this.apiBaseUrl,
      default_locale: this.defaultLocale,
      support_email: this.supportEmail,
      experimental_features: this.experimentalFeatures
    };
  }

  validate() {
    const errors = [];

    // Simple required checks
    if (!this.companyName.trim()) {
      errors.push({ loc: ['form', 'company_name'], msg: 'Company name is required.' });
    }

    // Domain validation (not too strict)
    // Allows optional http:// or https://, then domain-like structure
    const domainRegex = /^(https?:\/\/)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!this.primaryDomain.trim()) {
      errors.push({ loc: ['form', 'primary_domain'], msg: 'Primary domain is required.' });
    } else if (!domainRegex.test(this.primaryDomain)) {
      errors.push({ loc: ['form', 'primary_domain'], msg: 'Invalid domain format. Example: http://dashboard.skfx' });
    }

    // Color checks (just ensure it starts with # and has length)
    const colorRegex = /^#[0-9A-Fa-f]{3,8}$/;
    if (!colorRegex.test(this.primaryColor)) {
      errors.push({ loc: ['form', 'primary_color'], msg: 'Primary color must be a valid hex color (e.g. #000000).' });
    }
    if (!colorRegex.test(this.secondaryColor)) {
      errors.push({ loc: ['form', 'secondary_color'], msg: 'Secondary color must be a valid hex color (e.g. #ffffff).' });
    }

    // API base URL validation (basic check for URL)
    const urlRegex = /^https?:\/\/[^\s$.?#].[^\s]*$/i;
    if (!this.apiBaseUrl.trim()) {
      errors.push({ loc: ['form', 'api_base_url'], msg: 'API base URL is required.' });
    } else if (!urlRegex.test(this.apiBaseUrl)) {
      errors.push({ loc: ['form', 'api_base_url'], msg: 'API base URL must be a valid URL (e.g. http://api.example.com).' });
    }

    // Default locale required (not validating specific locale formats too strictly)
    if (!this.defaultLocale.trim()) {
      errors.push({ loc: ['form', 'default_locale'], msg: 'Default locale is required.' });
    }

    // Support email validation (simple email format)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!this.supportEmail.trim()) {
      errors.push({ loc: ['form', 'support_email'], msg: 'Support email is required.' });
    } else if (!emailRegex.test(this.supportEmail)) {
      errors.push({ loc: ['form', 'support_email'], msg: 'Invalid email format.' });
    }

    // Logo and background URLs are optional, but if provided, check basic format
    if (this.logoUrl && this.logoUrl.trim() && !urlRegex.test(this.logoUrl)) {
      errors.push({ loc: ['form', 'logo_url'], msg: 'Logo URL must be a valid URL if provided.' });
    }

    if (this.backgroundUrl && this.backgroundUrl.trim() && !urlRegex.test(this.backgroundUrl)) {
      errors.push({ loc: ['form', 'background_url'], msg: 'Background URL must be a valid URL if provided.' });
    }

    // Throw error if any validation failed
    if (errors.length > 0) {
      const validationError = new Error('Validation failed');
      validationError.details = errors;
      throw validationError;
    }
  }
}
